// src/components/ValidationDashboardPage.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import ValidationIntelligencePanel from "./validation/ValidationIntelligencePanel";
import ContextualExplainDrawer from "./ai/ContextualExplainDrawer";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdvisor } from "../AdvisorContext";
import "../dashboard.css";

const REQUEST_TIMEOUT_MS = 30000;

function safeNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function formatPercent(value, digits = 1) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return `${numeric.toFixed(digits)}%`;
}

function formatTimestamp(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString();
}

function formatRelativeTime(value) {
  if (!value) return "—";
  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) return "—";

  const diffMs = Date.now() - parsed;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

function formatApiErrorMessage(error) {
  const raw = String(error?.message || error || "").trim();
  if (!raw) return "Request failed.";

  const jsonStart = raw.indexOf("{");
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(jsonStart));
      if (parsed?.detail) return String(parsed.detail);
    } catch (_) {
      // Fall through to text cleanup.
    }
  }

  return raw
    .replace(/^400 Bad Request:\s*/i, "")
    .replace(/^403 Forbidden:\s*/i, "")
    .replace(/^500 Internal Server Error:\s*/i, "")
    .replace(
      /^verify X-Tenant-Id \/ X-User-Id headers and tenant membership\.\s*/i,
      ""
    )
    .trim();
}

function formatLabel(value, fallback = "Unknown") {
  const cleaned = String(value || "").trim();
  if (!cleaned) return fallback;
  return cleaned
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatEvidenceContext(item = {}) {
  const usage = formatLabel(item.usage_type, "").trim();
  const notes = String(item.notes || "").trim();

  const normalizedNotes = notes
    .toLowerCase()
    .replace(/[.]+$/g, "")
    .replace(/\s+/g, " ");

  const genericSourceStateNotes = new Set([
    "deterministic validation re-run using latest source-state loaded into oasys",
    "deterministic validation rerun using latest source-state loaded into oasys",
  ]);

  const conciseNotes = genericSourceStateNotes.has(normalizedNotes)
    ? "Latest source-state validation"
    : notes;

  return [usage, conciseNotes].filter(Boolean).join(" · ") || "Validation evidence";
}

function normalizeItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.recent_runs)) return payload.recent_runs;
  return [];
}

function validationTone(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["healthy", "strong", "passing", "passed", "clear"].includes(normalized)) return "low";
  if (["watchlist", "degraded", "warning", "partial"].includes(normalized)) return "medium";
  if (["attention_required", "failed", "blocked", "critical", "high_risk"].includes(normalized)) return "high";
  return "medium";
}

function confidenceTone(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "high") return "low";
  if (normalized === "medium") return "medium";
  if (normalized === "low") return "high";
  return "medium";
}

function statusTone(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["passed", "pass", "complete", "completed", "allowed", "fresh", "used"].includes(normalized)) return "low";
  if (["warning", "unknown", "pending", "partial", "review"].includes(normalized)) return "medium";
  if (["failed", "fail", "blocked", "missing", "stale", "error"].includes(normalized)) return "high";
  return "medium";
}

function StatusChip({ value, tone, size = "default" }) {
  const chipTone = tone || statusTone(value);

  if (size === "compact") {
    const color = chipTone === "low" ? "var(--ok)" : chipTone === "medium" ? "var(--warn)" : "#ff7b7b";
    const background =
      chipTone === "low"
        ? "color-mix(in srgb, var(--ok), transparent 86%)"
        : chipTone === "medium"
        ? "color-mix(in srgb, var(--warn), transparent 86%)"
        : "rgba(255, 123, 123, 0.12)";
    const border =
      chipTone === "low"
        ? "1px solid color-mix(in srgb, var(--ok), transparent 72%)"
        : chipTone === "medium"
        ? "1px solid color-mix(in srgb, var(--warn), transparent 74%)"
        : "1px solid rgba(255, 123, 123, 0.16)";

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "fit-content",
          minWidth: 0,
          minHeight: 0,
          height: 26,
          padding: "0 10px",
          borderRadius: 999,
          border,
          background,
          color,
          fontSize: 12,
          fontWeight: 800,
          lineHeight: 1,
          whiteSpace: "nowrap",
          letterSpacing: 0,
          textTransform: "none",
        }}
      >
        {formatLabel(value, "Unknown")}
      </span>
    );
  }

  return <span className={`risk-pill risk-${chipTone}`}>{formatLabel(value, "Unknown")}</span>;
}

function MetricTile({ label, value, detail, tone = "default" }) {
  const color =
    tone === "danger"
      ? "var(--danger)"
      : tone === "warn"
      ? "var(--warn)"
      : tone === "ok"
      ? "var(--ok)"
      : "var(--text)";

  return (
    <div className="validation-metric-tile">
      <div className="validation-metric-label">{label}</div>
      <div className="validation-metric-value" style={{ color }}>
        {value}
      </div>
      {detail ? <div className="validation-metric-detail">{detail}</div> : null}
    </div>
  );
}

function EmptyState({ children = "No records available." }) {
  return <div className="muted" style={{ padding: "12px 0" }}>{children}</div>;
}

function SectionCard({ title, subtitle, children, className = "" }) {
  return (
    <div className={`card validation-context-card ${className}`.trim()}>
      <div className="card-header validation-context-card__header">
        <div>
          <div className="card-title">{title}</div>
          {subtitle ? <div className="connector-health-subtitle">{subtitle}</div> : null}
        </div>
      </div>
      <div className="card-body validation-context-card__body">
        {children}
      </div>
    </div>
  );
}

function ValidationRow({ item, onOpenPosture, onExplain }) {
  const title = [item.framework, item.control_id, item.rule_id].filter(Boolean).join(" · ");
  const resourceContext = [item.resource_id, item.provider].filter(Boolean).join(" · ") || "No resource context";
  const validatedAt = item.validated_at || item.completed_at || item.created_at;

  return (
    <div className="validation-queue-row">
      <div className="validation-queue-row__identity">
        <strong>{title || item.id || "Validation result"}</strong>
        <span>{resourceContext}</span>
      </div>

      <div className="validation-queue-row__reason">
        {item.reason || item.summary || "No validation reason provided."}
      </div>

      <div className="validation-queue-row__time">
        <span>Validated</span>
        <strong>{formatRelativeTime(validatedAt)}</strong>
      </div>

      <div className="validation-queue-row__state">
        <StatusChip value={item.result || item.status} size="compact" />
      </div>

      <div
        className="validation-queue-row__action"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {typeof onOpenPosture === "function" ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() =>
              onOpenPosture({
                framework: item.framework || undefined,
                controlId: item.control_id || undefined,
                ruleId: item.rule_id || undefined,
                resourceId: item.resource_id || undefined,
                section: "top_risks",
              })
            }
          >
            Open Posture
          </button>
        ) : null}

        {typeof onExplain === "function" ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onExplain(item)}
          >
            Explain
          </button>
        ) : null}
      </div>
    </div>
  );
}

function TimelineRow({ item, type = "run" }) {
  const isRun = type === "run";
  const status = item.result_summary || item.result || item.status || item.latest_status;
  const title = isRun
    ? [item.framework, item.control_id, item.rule_id].filter(Boolean).join(" · ") || item.trigger_source || "Validation run"
    : [item.framework, item.control_id, item.rule_id].filter(Boolean).join(" · ") || item.resource_id || "Validation history";

  return (
    <div className="validation-timeline-row">
      <div className="validation-timeline-row__copy">
        <strong>{title}</strong>
        <span>
          {isRun
            ? [item.resource_id, item.provider, item.trigger_source].filter(Boolean).join(" · ")
            : [item.resource_id, item.provider, item.stream, item.trend].filter(Boolean).join(" · ")}
        </span>
        <span>
          {formatTimestamp(item.completed_at || item.validated_at || item.last_validated_at || item.created_at)}
        </span>
      </div>
      <StatusChip value={status} size="compact" />
    </div>
  );
}

function EvidenceRow({ item }) {
  return (
    <div className="validation-evidence-row">
      <div className="validation-evidence-row__copy">
        <strong>{item.evidence_source || item.evidence_id || "Evidence link"}</strong>
        <span>{formatEvidenceContext(item)}</span>
      </div>
      <div className="validation-evidence-row__statuses">
        <StatusChip value={item.completeness_status || "unknown"} size="compact" />
        <StatusChip value={item.freshness_status || "unknown"} size="compact" />
      </div>
    </div>
  );
}

export default function ValidationDashboardPage({
  apiBaseUrl,
  getAccessToken,
  userProfile = null,
  routeParams = {},
  setMode,
  themeMode = "light",
}) {
  const { getAccessTokenSilently } = useAuth0();
  const { setCopilotContext, clearCopilotContext } = useAdvisor();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [runs, setRuns] = useState([]);
  const [results, setResults] = useState([]);
  const [controlHistory, setControlHistory] = useState([]);
  const [resourceHistory, setResourceHistory] = useState([]);
  const [evidenceLinks, setEvidenceLinks] = useState([]);
  const [confidence, setConfidence] = useState(null);
  const [validationIntelligence, setValidationIntelligence] = useState({
    question: "What needs attention now?",
    card: null,
    metadata: null,
    loading: false,
    error: "",
  });

  const [validationExplain, setValidationExplain] = useState({
    open: false,
    record: null,
    question: "Why did it fail?",
    card: null,
    metadata: null,
    loading: false,
    error: "",
  });

  const normalizedBase = useMemo(() => {
    const explicitBase = String(apiBaseUrl || "").trim();
    const runtimeBase =
      typeof window !== "undefined" ? String(window.__V1_BASE || "").trim() : "";
    const rawBase = explicitBase || runtimeBase;

    if (!rawBase) {
      throw new Error(
        "OASYS API base is unavailable. App runtime configuration must initialize the API authority."
      );
    }

    const trimmed = rawBase.replace(/\/+$/, "");
    return trimmed.toLowerCase().endsWith("/v1") ? trimmed : `${trimmed}/v1`;
  }, [apiBaseUrl]);

  const tenantId =
    userProfile?.tenant_id ||
    userProfile?.tenantId ||
    process.env.REACT_APP_TENANT_ID ||
    "";

  const userId =
    userProfile?.user_id ||
    userProfile?.userId ||
    userProfile?.id ||
    userProfile?.sub ||
    process.env.REACT_APP_USER_ID ||
    "";

  const frameworkFilter = routeParams?.framework || "";

  const fetchToken = useCallback(async () => {
    if (typeof getAccessToken === "function") {
      return await getAccessToken();
    }

    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    return await getAccessTokenSilently(
      audience ? { authorizationParams: { audience } } : undefined
    );
  }, [getAccessToken, getAccessTokenSilently]);

  const getJsonWithToken = useCallback(
    async (token, path, params = {}, signal) => {
      const searchParams = new URLSearchParams();
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      const query = searchParams.toString();
      const buildUrl = (base) => (query ? `${base}${path}?${query}` : `${base}${path}`);

      const urls = [buildUrl(normalizedBase)];

      // Local React development may proxy /v1 through the frontend server.
      // Keep that retry dev-only so production never masks API configuration
      // or routing failures by silently falling back to the app origin.
      if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
        const sameOriginBase = "/v1";
        const sameOriginUrl = buildUrl(sameOriginBase);
        if (!urls.includes(sameOriginUrl)) urls.push(sameOriginUrl);
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const errors = [];

      for (const url of urls) {
        let res;
        try {
          res = await fetch(url, { method: "GET", headers, signal });
        } catch (fetchError) {
          errors.push(`${url}: ${String(fetchError?.message || fetchError || "network error")}`);
          continue;
        }

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          errors.push(`${res.status} ${res.statusText} from ${url}: ${text}`);
          continue;
        }

        try {
          return await res.json();
        } catch (jsonError) {
          errors.push(`${url}: response was not valid JSON (${String(jsonError?.message || jsonError)})`);
        }
      }

      throw new Error(`Validation API request failed. Tried: ${errors.join(" | ")}`);
    },
    [normalizedBase, tenantId, userId]
  );

  const postJsonWithToken = useCallback(
    async (token, path, body = {}, signal) => {
      const buildUrl = (base) => `${base}${path}`;
      const urls = [buildUrl(normalizedBase)];

      if (typeof window !== "undefined") {
        const sameOriginUrl = buildUrl("/v1");
        if (!urls.includes(sameOriginUrl)) urls.push(sameOriginUrl);
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const errors = [];

      for (const url of urls) {
        let res;

        try {
          res = await fetch(url, {
            method: "POST",
            headers,
            signal,
            body: JSON.stringify(body || {}),
          });
        } catch (fetchError) {
          errors.push(
            `${url}: ${String(
              fetchError?.message || fetchError || "network error"
            )}`
          );
          continue;
        }

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          errors.push(`${res.status} ${res.statusText} from ${url}: ${text}`);
          continue;
        }

        try {
          return await res.json();
        } catch (jsonError) {
          errors.push(
            `${url}: response was not valid JSON (${String(
              jsonError?.message || jsonError
            )})`
          );
        }
      }

      throw new Error(
        `Validation Intelligence request failed. Tried: ${errors.join(" | ")}`
      );
    },
    [normalizedBase, tenantId, userId]
  );

  useEffect(() => {
    let mounted = true;
    const activeControllers = new Set();

    setLoading(true);
    setErr(null);
    setWarnings([]);

    const runRequest = async (request) => {
      const controller = new AbortController();
      activeControllers.add(controller);
      const timeoutId = setTimeout(() => {
        controller.abort(`${request.label} timed out after ${REQUEST_TIMEOUT_MS / 1000}s`);
      }, REQUEST_TIMEOUT_MS);

      try {
        return await request.load(controller.signal);
      } finally {
        clearTimeout(timeoutId);
        activeControllers.delete(controller);
      }
    };

    (async () => {
      try {
        if (!tenantId) throw new Error("Validation Dashboard requires X-Tenant-Id.");
        const token = await fetchToken();
        if (!token) return;

        const params = frameworkFilter ? { framework: frameworkFilter } : {};
        const requests = [
          {
            key: "dashboard",
            required: true,
            label: "Validation dashboard",
            load: (signal) => getJsonWithToken(token, "/validation/dashboard", params, signal),
          },
          {
            key: "runs",
            required: false,
            label: "Validation runs",
            load: (signal) => getJsonWithToken(token, "/validation/runs", { ...params, limit: 25 }, signal),
          },
          {
            key: "results",
            required: false,
            label: "Validation results",
            load: (signal) => getJsonWithToken(token, "/validation/results", { ...params, limit: 25 }, signal),
          },
          {
            key: "controlHistory",
            required: false,
            label: "Control history",
            load: (signal) => getJsonWithToken(token, "/validation/history/control", { ...params, limit: 25 }, signal),
          },
          {
            key: "resourceHistory",
            required: false,
            label: "Resource history",
            load: (signal) => getJsonWithToken(token, "/validation/history/resource", { ...params, limit: 25 }, signal),
          },
          {
            key: "evidenceLinks",
            required: false,
            label: "Evidence links",
            load: (signal) => getJsonWithToken(token, "/validation/evidence-links", { limit: 25 }, signal),
          },
          {
            key: "confidence",
            required: false,
            label: "Confidence",
            load: (signal) => getJsonWithToken(token, "/validation/confidence", { ...params, confidence_scope: "run" }, signal),
          },
        ];

        const settled = await Promise.allSettled(requests.map((request) => runRequest(request)));
        if (!mounted) return;

        const nextWarnings = [];
        const next = {
          dashboard: null,
          runs: [],
          results: [],
          controlHistory: [],
          resourceHistory: [],
          evidenceLinks: [],
          confidence: null,
        };

        settled.forEach((result, index) => {
          const request = requests[index];
          if (result.status === "fulfilled") {
            const value = result.value;
            if (request.key === "dashboard") next.dashboard = value || null;
            if (request.key === "runs") next.runs = normalizeItems(value);
            if (request.key === "results") next.results = normalizeItems(value);
            if (request.key === "controlHistory") next.controlHistory = normalizeItems(value);
            if (request.key === "resourceHistory") next.resourceHistory = normalizeItems(value);
            if (request.key === "evidenceLinks") next.evidenceLinks = normalizeItems(value);
            if (request.key === "confidence") next.confidence = value?.item || null;
            return;
          }

          const message = String(result.reason?.message || result.reason || `${request.label} failed.`);
          nextWarnings.push(`${request.label} unavailable: ${message}`);
        });

        if (!next.dashboard) {
          throw new Error(nextWarnings[0] || "Validation dashboard failed to load.");
        }

        setDashboard(next.dashboard);
        setRuns(next.runs);
        setResults(next.results);
        setControlHistory(next.controlHistory);
        setResourceHistory(next.resourceHistory);
        setEvidenceLinks(next.evidenceLinks);
        setConfidence(next.confidence);
        setWarnings(nextWarnings);
      } catch (error) {
        if (!mounted) return;
        console.error("Validation Dashboard load error:", error);
        setErr(String(error?.message || error));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      activeControllers.forEach((controller) => controller.abort("validation-dashboard-unmounted"));
      activeControllers.clear();
    };
  }, [fetchToken, frameworkFilter, getJsonWithToken, tenantId]);

  const metrics = dashboard?.metrics || dashboard || {};
  const summary = dashboard?.summary || {};
  const sections = useMemo(() => dashboard?.sections || {}, [dashboard]);
  const complianceConfidence =
    summary?.compliance_confidence || metrics?.compliance_confidence || confidence || {};
  const evidenceCoverage = sections?.evidence_coverage || metrics?.evidence_coverage || {};
  const controlSummary = sections?.control_health?.summary || metrics?.control_health?.summary || {};
  const resourceSummary = sections?.resource_health?.summary || metrics?.resource_health?.summary || {};
  const closure = metrics?.closure || {};

  const failedValidations = useMemo(() => {
    const sectionRows = Array.isArray(sections?.failed_validations) ? sections.failed_validations : [];
    const failedRows = results.filter((item) => {
      const status = String(item.result || item.status || "").toLowerCase();
      return status === "failed" || status === "fail" || status === "error";
    });
    return sectionRows.length ? sectionRows : failedRows;
  }, [results, sections]);

  const askValidationIntelligence = useCallback(
    async (question = "What needs attention now?", signal) => {
      const normalizedQuestion =
        String(question || "").trim() || "What needs attention now?";

      setValidationIntelligence((current) => ({
        ...current,
        question: normalizedQuestion,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchToken();
        if (!token) throw new Error("No access token was available.");

        const payload = await postJsonWithToken(
          token,
          "/ai/copilot/ask",
          {
            question: normalizedQuestion,
            scope: "validation",
            copilot_context: {
              page: "validation",
              surface: "validation_page",
              intelligence_mode: "page",
              ...(frameworkFilter ? { framework: frameworkFilter } : {}),
            },
          },
          signal
        );

        const card =
          payload?.card && typeof payload.card === "object"
            ? payload.card
            : null;

        if (!card) {
          throw new Error(
            "OASYS Intelligence returned no grounded Validation page card."
          );
        }

        setValidationIntelligence({
          question: normalizedQuestion,
          card,
          metadata:
            card?.metadata && typeof card.metadata === "object"
              ? card.metadata
              : payload?.metadata || null,
          loading: false,
          error: "",
        });

        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          setValidationIntelligence((current) => ({
            ...current,
            loading: false,
            error:
              "OASYS Intelligence is temporarily unavailable. " +
              "Deterministic validation health, evidence, and closure metrics remain authoritative.",
          }));
        }

        return null;
      } finally {
        if (!signal?.aborted) {
          setValidationIntelligence((current) => ({
            ...current,
            loading: false,
          }));
        }
      }
    },
    [fetchToken, frameworkFilter, postJsonWithToken]
  );

  useEffect(() => {
    if (loading || err || !dashboard) return undefined;

    const controller = new AbortController();
    askValidationIntelligence("What needs attention now?", controller.signal);

    return () => {
      controller.abort("validation-intelligence-page-context-changed");
    };
  }, [
    askValidationIntelligence,
    dashboard,
    err,
    loading,
  ]);

  const closeValidationExplain = useCallback(() => {
    setValidationExplain((current) => ({
      ...current,
      open: false,
      loading: false,
      error: "",
    }));
  }, []);

  const explainValidation = useCallback(
    async (validation, question = "Why did it fail?", signal) => {
      const cleanedQuestion = String(question || "").trim();
      if (!validation || !cleanedQuestion || !tenantId) return null;

      const selectedResultId =
        validation?.validation_result_id ||
        validation?.id ||
        null;

      const selectedRunId =
        validation?.validation_run_id ||
        validation?.run_id ||
        null;

      const sameRecord = Boolean(
        validationExplain.record &&
          (
            (selectedResultId &&
              String(
                validationExplain.record?.validation_result_id ||
                  validationExplain.record?.id ||
                  ""
              ) === String(selectedResultId)) ||
            (!selectedResultId &&
              String(validationExplain.record?.validation_run_id || "") ===
                String(selectedRunId || "") &&
              String(validationExplain.record?.rule_id || "") ===
                String(validation?.rule_id || "") &&
              String(validationExplain.record?.resource_id || "") ===
                String(validation?.resource_id || ""))
          )
      );

      setValidationExplain((current) => ({
        ...current,
        open: true,
        record: validation,
        question: cleanedQuestion,
        card: sameRecord ? current.card : null,
        metadata: sameRecord ? current.metadata : null,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchToken();
        if (!token) throw new Error("No access token was available.");

        const selectedFramework =
          validation?.framework ||
          frameworkFilter ||
          undefined;

        const response = await postJsonWithToken(
          token,
          "/ai/copilot/ask",
          {
            question: cleanedQuestion,
            scope: "validation",
            copilot_context: {
              page: "validation",
              surface: "validation_page",
              intelligence_mode: "contextual_explain",
              record_type: "validation",
              ...(selectedResultId
                ? { validation_result_id: selectedResultId }
                : {}),
              ...(selectedRunId
                ? { validation_run_id: selectedRunId }
                : {}),
              ...(validation?.remediation_task_id || validation?.task_id
                ? {
                    task_id:
                      validation?.remediation_task_id ||
                      validation?.task_id,
                  }
                : {}),
              ...(selectedFramework
                ? { framework: selectedFramework }
                : {}),
              ...(validation?.control_id
                ? { control_id: validation.control_id }
                : {}),
              ...(validation?.rule_id
                ? { rule_id: validation.rule_id }
                : {}),
              ...(validation?.resource_id
                ? { resource_id: validation.resource_id }
                : {}),
              ...(validation?.provider
                ? { provider: validation.provider }
                : {}),
              ...(validation?.stream
                ? { stream: validation.stream }
                : {}),
              ...(validation?.result || validation?.status
                ? {
                    validation_status:
                      validation?.result || validation?.status,
                  }
                : {}),
            },
          },
          signal
        );

        const card =
          response?.card && typeof response.card === "object"
            ? response.card
            : null;

        if (!card) {
          throw new Error(
            "OASYS Intelligence returned no grounded explanation for this selected validation."
          );
        }

        setValidationExplain({
          open: true,
          record: validation,
          question: cleanedQuestion,
          card,
          metadata:
            card?.metadata && typeof card.metadata === "object"
              ? card.metadata
              : response?.metadata || null,
          loading: false,
          error: "",
        });

        return card;
      } catch (error) {
        if (error?.name === "AbortError") return null;

        setValidationExplain((current) => ({
          ...current,
          open: true,
          record: validation,
          question: cleanedQuestion,
          loading: false,
          error: formatApiErrorMessage(error),
        }));

        return null;
      }
    },
    [
      fetchToken,
      frameworkFilter,
      postJsonWithToken,
      tenantId,
      validationExplain.record,
    ]
  );

  const validationExplainLabel = useMemo(() => {
    const record = validationExplain.record;
    if (!record) return "";

    const ruleLabel = formatLabel(
      record.rule_id || record.control_id,
      "Validation"
    );
    const resourceId = String(record.resource_id || "").trim();

    return resourceId
      ? `${ruleLabel} validation · ${resourceId}`
      : `${ruleLabel} validation`;
  }, [validationExplain.record]);

  useEffect(() => {
    const record =
      validationExplain.open && validationExplain.record
        ? validationExplain.record
        : null;
    const selectedResultId = String(
      record?.validation_result_id || record?.id || ""
    ).trim();
    const selectedRunId = String(
      record?.validation_run_id || record?.run_id || ""
    ).trim();

    const pageContext = {
      page: "validation",
      surface: "validation_page",
      intelligence_mode: record ? "contextual_explain" : "page",
      ...(frameworkFilter ? { framework: frameworkFilter } : {}),
      ...(routeParams?.section ? { section: routeParams.section } : {}),
    };

    if (!record || (!selectedResultId && !selectedRunId)) {
      clearCopilotContext();
      setCopilotContext(pageContext);
      return;
    }

    setCopilotContext({
      ...pageContext,
      record_type: selectedResultId ? "validation_result" : "validation_run",
      ...(selectedResultId
        ? { validation_result_id: selectedResultId }
        : {}),
      ...(selectedRunId ? { validation_run_id: selectedRunId } : {}),
      focus_label: validationExplainLabel || "Selected validation",
      focus_type: selectedResultId ? "Validation result" : "Validation run",
    });
  }, [
    clearCopilotContext,
    frameworkFilter,
    routeParams?.section,
    setCopilotContext,
    validationExplain.open,
    validationExplain.record,
    validationExplainLabel,
  ]);

  const validationHealth = metrics?.validation_health || summary?.validation_health || "unknown";
  const healthTone = validationTone(validationHealth);
  const confidenceLevel = complianceConfidence?.level || complianceConfidence?.confidence_level || "unknown";
  const confidenceScore = safeNumber(
    complianceConfidence?.score ?? complianceConfidence?.confidence_score,
    0
  );

  const openPosture = useCallback(
    (params = {}) => {
      if (typeof setMode !== "function") return;
      setMode("posture", {
        ...params,
        source: "validation_dashboard",
        returnMode: "validation",
        returnTo: "validation_dashboard",
        routeNavNonce: String(Date.now()),
      });
    },
    [setMode]
  );

  if (loading) {
    return (
      <div className={`oasys-dashboard validation-intelligence-page ${themeMode === "dark" ? "dark" : ""}`}>
        <div className="dash-shell validation-intelligence-shell">
          <div className="dash-header">
            <div>
              <div className="validation-state-title">Validation Intelligence</div>
            </div>
          </div>
          <div className="card" style={{ padding: 20, marginTop: 16 }}>
            Loading Validation Intelligence…
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className={`oasys-dashboard validation-intelligence-page ${themeMode === "dark" ? "dark" : ""}`}>
        <div className="dash-shell validation-intelligence-shell">
          <div className="dash-header">
            <div>
              <div className="validation-state-title">Validation Intelligence</div>
            </div>
          </div>
          <div className="card" style={{ padding: 20, marginTop: 16 }}>
            <div style={{ color: "#ff7b7b" }}>Failed to load Validation Intelligence: {err}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`oasys-dashboard validation-intelligence-page ${themeMode === "dark" ? "dark" : ""}`}>
      <div className="dash-shell validation-intelligence-shell">
        <header className="validation-page-header">
          <div className="validation-page-header__content">
            <h1>Validation Intelligence</h1>
            <p>
              Validation health, Compliance Confidence, evidence coverage, closure readiness,
              and failed validation timelines.
            </p>
          </div>
          <div className="validation-page-header__actions">
            <StatusChip value={validationHealth} tone={healthTone} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMode?.("dashboard")}>
              Mission Control
            </button>
          </div>
        </header>

        {warnings.length > 0 ? (
          <div className="card" style={{ padding: "10px 14px", marginBottom: 16 }}>
            <strong style={{ color: "var(--warn)", fontSize: 13 }}>Loaded with partial data</strong>
            <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 6 }}>
              {warnings.slice(0, 3).join(" · ")}
            </div>
          </div>
        ) : null}

        <ValidationIntelligencePanel
          question={validationIntelligence.question}
          card={validationIntelligence.card}
          metadata={validationIntelligence.metadata}
          loading={validationIntelligence.loading}
          error={validationIntelligence.error}
          onAsk={askValidationIntelligence}
        />

        <section className="card validation-health-card">
          <div className="card-header validation-health-card__header">
            <div>
              <div className="card-title">Validation Health</div>
              <div className="connector-health-subtitle">
                {safeNumber(summary.total_results ?? metrics?.result_totals?.total_results)} validation result
                {safeNumber(summary.total_results ?? metrics?.result_totals?.total_results) === 1 ? "" : "s"} · {safeNumber(summary.failed_results ?? metrics?.result_totals?.failed_results)} failed
              </div>
            </div>
            <StatusChip value={validationHealth} tone={healthTone} />
          </div>

          <div className="validation-health-metrics">
            <MetricTile
              label="Compliance Confidence"
              value={confidenceScore ? `${confidenceScore.toFixed(0)}%` : "—"}
              detail={formatLabel(confidenceLevel, "unknown")}
              tone={confidenceTone(confidenceLevel) === "low" ? "ok" : confidenceTone(confidenceLevel) === "medium" ? "warn" : "danger"}
            />
            <MetricTile
              label="Success Rate"
              value={formatPercent(summary.success_rate ?? metrics?.success_rate)}
              detail={`${formatPercent(summary.failure_rate ?? metrics?.failure_rate)} failure`}
              tone={safeNumber(summary.failure_rate ?? metrics?.failure_rate) > 0 ? "danger" : "ok"}
            />
            <MetricTile
              label="Evidence Coverage"
              value={formatPercent(evidenceCoverage.completeness_pct, 0)}
              detail={`${formatPercent(evidenceCoverage.freshness_pct, 0)} fresh`}
              tone={safeNumber(evidenceCoverage.completeness_pct) >= 90 ? "ok" : "warn"}
            />
            <MetricTile
              label="Closure Blockers"
              value={safeNumber(closure.pending_or_blocked ?? closure.closure_blocked)}
              detail={`${safeNumber(closure.closure_allowed)} ready to close`}
              tone={safeNumber(closure.pending_or_blocked ?? closure.closure_blocked) > 0 ? "danger" : "ok"}
            />
            <MetricTile
              label="Failed Controls"
              value={safeNumber(controlSummary.failing_controls)}
              detail={`${safeNumber(controlSummary.total_controls)} total tracked`}
              tone={safeNumber(controlSummary.failing_controls) > 0 ? "danger" : "ok"}
            />
            <MetricTile
              label="Failed Resources"
              value={safeNumber(resourceSummary.failing_resources)}
              detail={`${safeNumber(resourceSummary.total_resources)} total tracked`}
              tone={safeNumber(resourceSummary.failing_resources) > 0 ? "danger" : "ok"}
            />
            <MetricTile
              label="Pending Runs"
              value={safeNumber(summary.pending_runs ?? metrics?.run_totals?.pending_runs)}
              detail="awaiting completion"
              tone={safeNumber(summary.pending_runs ?? metrics?.run_totals?.pending_runs) > 0 ? "warn" : "ok"}
            />
          </div>
        </section>

        <section className="card validation-failed-queue">
          <div className="card-header validation-failed-queue__header">
            <div>
              <div className="card-title">Failed Validation Queue</div>
              <div className="connector-health-subtitle">
                Highest-priority failed validations requiring posture review.
              </div>
            </div>
            <span className="validation-failed-queue__count">
              {failedValidations.length} failed
            </span>
          </div>
          <div className="validation-failed-queue__columns" aria-hidden="true">
            <span>Control / Resource</span>
            <span>Reason</span>
            <span>Timing</span>
            <span>State</span>
            <span>Action</span>
          </div>
          <div className="validation-failed-queue__body">
            {failedValidations.length === 0 ? (
              <EmptyState>No failed validations are currently visible.</EmptyState>
            ) : (
              failedValidations.slice(0, 5).map((item) => (
                <ValidationRow
                  key={item.id || item.validation_result_id}
                  item={item}
                  onOpenPosture={openPosture}
                  onExplain={(validation) =>
                    explainValidation(validation, "Why did it fail?")
                  }
                />
              ))
            )}
          </div>
        </section>

        <section className="validation-context-grid" aria-label="Validation operational context">
          <SectionCard
            title="Recent Validation Runs"
            subtitle="Newest validation activity across controls and resources."
            className="validation-context-card--runs"
          >
            {runs.length === 0 ? (
              <EmptyState>No validation runs found.</EmptyState>
            ) : (
              runs.slice(0, 8).map((item) => <TimelineRow key={item.id} item={item} type="run" />)
            )}
          </SectionCard>

          <SectionCard
            title="Control Timeline"
            subtitle="Latest control validation history."
            className="validation-context-card--controls"
          >
            {controlHistory.length === 0 ? (
              <EmptyState>No control validation history found.</EmptyState>
            ) : (
              controlHistory.slice(0, 8).map((item) => <TimelineRow key={item.id} item={item} type="history" />)
            )}
          </SectionCard>
        </section>

        <section className="validation-context-grid" aria-label="Validation evidence context">
          <SectionCard
            title="Resource Timeline"
            subtitle="Resource-specific validation state."
            className="validation-context-card--resources"
          >
            {resourceHistory.length === 0 ? (
              <EmptyState>No resource validation history found.</EmptyState>
            ) : (
              resourceHistory.slice(0, 8).map((item) => <TimelineRow key={item.id} item={item} type="history" />)
            )}
          </SectionCard>

          <SectionCard
            title="Evidence Links"
            subtitle="Evidence used to support validation conclusions."
            className="validation-context-card--evidence"
          >
            {evidenceLinks.length === 0 ? (
              <EmptyState>No validation evidence links found.</EmptyState>
            ) : (
              evidenceLinks.slice(0, 8).map((item) => <EvidenceRow key={item.id} item={item} />)
            )}
          </SectionCard>
        </section>

        <ContextualExplainDrawer
          open={validationExplain.open}
          record={validationExplain.record}
          recordLabel={validationExplainLabel}
          question={validationExplain.question}
          card={validationExplain.card}
          metadata={validationExplain.metadata}
          loading={validationExplain.loading}
          error={validationExplain.error}
          themeMode={themeMode}
          contextDescription="Grounded reasoning for this selected validation result. Deterministic OASYS validation state remains authoritative."
          prompts={[
            "Why did it fail?",
            "What is still unresolved?",
            "What evidence is missing?",
            "What should I verify before trying again?",
          ]}
          onAsk={(question) =>
            explainValidation(validationExplain.record, question)
          }
          onClose={closeValidationExplain}
        />
      </div>
    </div>
  );
}
